import { useMemo, useState } from "react";
import { ArrowUpRight, Plus, X } from "lucide-react";
import { money } from "../lib/api";

export default function ReceiptForm({ vendors = [], items = [], onSubmit, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    vendor: vendors[0]?.name || "",
    invoice_number: "",
    invoice_date: today,
    received_by: "Store In-charge",
    payment_status: "Unpaid",
    amount_paid: 0,
    notes: "",
  });
  const [lines, setLines] = useState([{ item: items[0]?.name || "", batch: "", qty: 1, unit: items[0]?.base_unit || "", rate: items[0]?.rate || 0, expiry: "" }]);
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const addLine = () => setLines((l) => [...l, { item: items[0]?.name || "", batch: "", qty: 1, unit: items[0]?.base_unit || "", rate: 0, expiry: "" }]);
  const updateLine = (i, k, v) => setLines((l) => l.map((row, idx) => {
    if (idx !== i) return row;
    const updated = { ...row, [k]: v };
    if (k === "item") {
      const item = items.find((x) => x.name === v);
      if (item) { updated.unit = item.base_unit; updated.rate = item.rate; }
    }
    return updated;
  }));
  const removeLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const total = useMemo(() => lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.rate || 0), 0), [lines]);
  const unitOptions = (name) => {
    const item = items.find((x) => x.name === name);
    return item?.units?.map((u) => u.name) || [item?.base_unit || ""];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit({
        ...form,
        amount_paid: Number(form.amount_paid || 0),
        items: lines.filter((l) => l.item).map((l) => ({ ...l, qty: Number(l.qty), rate: Number(l.rate) })),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-grid">
        <label>Vendor
          <select data-testid="vendor-select" value={form.vendor} onChange={(e) => update("vendor", e.target.value)}>
            {vendors.map((v) => <option key={v.id || v.name}>{v.name}</option>)}
          </select>
        </label>
        <label>Invoice number<input data-testid="invoice_number-input" value={form.invoice_number} onChange={(e) => update("invoice_number", e.target.value)} /></label>
        <label>Invoice date<input data-testid="invoice_date-input" type="date" value={form.invoice_date} onChange={(e) => update("invoice_date", e.target.value)} /></label>
        <label>Received by<input data-testid="received_by-input" value={form.received_by} onChange={(e) => update("received_by", e.target.value)} /></label>
        <label>Payment status
          <select data-testid="payment_status-select" value={form.payment_status} onChange={(e) => update("payment_status", e.target.value)}>
            <option>Unpaid</option><option>Paid</option><option>Partial</option>
          </select>
        </label>
        <label>Amount paid (₹)<input data-testid="amount_paid-input" type="number" value={form.amount_paid} onChange={(e) => update("amount_paid", e.target.value)} /></label>
      </div>

      <div className="units-block">
        <div className="units-head">
          <div>
            <p className="eyebrow">LINE ITEMS</p>
            <h4>What did you receive?</h4>
          </div>
          <button type="button" className="secondary sm" data-testid="add-line-button" onClick={addLine}><Plus size={14}/> Add line</button>
        </div>
        <div className="unit-rows">
          {lines.map((l, i) => (
            <div className="line-row receipt-line" key={i}>
              <select data-testid={`line-item-${i}`} value={l.item} onChange={(e) => updateLine(i, "item", e.target.value)}>
                {items.map((it) => <option key={it.id || it.name}>{it.name}</option>)}
              </select>
              <input placeholder="Batch" data-testid={`line-batch-${i}`} value={l.batch} onChange={(e) => updateLine(i, "batch", e.target.value)} />
              <input placeholder="Qty" data-testid={`line-qty-${i}`} type="number" value={l.qty} onChange={(e) => updateLine(i, "qty", e.target.value)} />
              <select data-testid={`line-unit-${i}`} value={l.unit} onChange={(e) => updateLine(i, "unit", e.target.value)}>
                {unitOptions(l.item).map((u) => <option key={u}>{u}</option>)}
              </select>
              <input placeholder="Rate" data-testid={`line-rate-${i}`} type="number" step="0.01" value={l.rate} onChange={(e) => updateLine(i, "rate", e.target.value)} />
              <input placeholder="Expiry" data-testid={`line-expiry-${i}`} type="date" value={l.expiry} onChange={(e) => updateLine(i, "expiry", e.target.value)} />
              <button type="button" className="icon-btn ghost" onClick={() => removeLine(i)}><X size={14}/></button>
            </div>
          ))}
        </div>
        <div className="receipt-total" data-testid="receipt-total">
          <span>Invoice total</span>
          <strong>{money(total)}</strong>
        </div>
      </div>

      <div className="modal-actions">
        <button type="button" className="secondary" onClick={onClose}>Cancel</button>
        <button className="primary" data-testid="form-submit-button" disabled={busy}>{busy ? "Saving…" : "Save receipt"} <ArrowUpRight size={16}/></button>
      </div>
    </form>
  );
}
